import { useMemo } from 'react';
import type { AltYCanvasContent } from '../types/global';

export type WorkspaceCanvasBadge = 'writing' | 'done' | null;

type WorkspaceCanvasProps = {
  // Latest canvas payload the AI emitted, or null. Null renders the
  // "ask the AI to render something here" empty state.
  content: AltYCanvasContent | null;
  // Compact header badge while the model streams a canvas block, then briefly
  // when the new payload lands. Null hides the badge.
  badge?: WorkspaceCanvasBadge;
  // More than one configured env tab — show the canvas title as a switchable tab
  // so the user can jump back to an environment. With a single env, the header
  // stays minimal (Workspace label + Live + close only).
  multiEnv?: boolean;
  // Optional close affordance. Wired by App.tsx to re-enable an env tab (the
  // user dismissing the canvas is equivalent to picking an environment).
  onClose?: () => void;
};

// Minimal HTML scaffold around the AI-supplied body so it inherits the app's
// font + a clean default. Sandbox blocks scripts; allow-same-origin is required
// so <img src="/api/alty/generated/..."> requests carry the host session cookie.
// OneSheet / Flatly tokens — matches Alt-Y shell CSS and online21 Bootstrap theme.
const CANVAS_BASE_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,400;0,700;0,900;1,400&display=swap');
:root{
  color-scheme:light;
  --os-primary:#2c3e50;
  --os-success:#18bc9c;
  --os-muted:#6c757d;
  --os-border:#dee2e6;
  --os-panel:#f8f9fa;
  --os-purple:#bf7fff;
  --os-hover:#e0efff;
  --os-active:#cde6ff;
  --os-radius:0.25rem;
}
html,body{margin:0;padding:0;background:#fff;color:var(--os-primary);
  font:14px/1.45 "Lato",-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif;
  -webkit-font-smoothing:antialiased;}
body{padding:20px 22px 32px;}
img{max-width:100%;height:auto;}
a{color:var(--os-success);}
.os-section{font-size:10px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;color:var(--os-purple);margin:0 0 10px;}
.os-lead{color:var(--os-muted);margin:0 0 22px;line-height:1.55;max-width:52em;}
.os-grid{display:grid;gap:22px;}
.os-card{border:1px solid var(--os-border);border-radius:8px;background:#fff;
  box-shadow:0 2px 5px rgba(0,0,0,.08);overflow:hidden;}
.os-card-hd{padding:12px 14px;border-bottom:1px solid var(--os-border);background:var(--os-panel);
  font-weight:700;font-size:13px;}
.os-card-bd{padding:14px;}
.os-btn{display:inline-flex;align-items:center;gap:6px;padding:6px 12px;border-radius:var(--os-radius);
  border:1px solid transparent;font:inherit;font-size:13px;font-weight:700;line-height:1.4;cursor:default;
  text-decoration:none;color:#fff;background:var(--os-primary);}
.os-btn-success{background:var(--os-success);border-color:var(--os-success);}
.os-btn-outline{color:var(--os-primary);background:#fff;border-color:var(--os-border);}
.os-btn-sm{padding:4px 10px;font-size:12px;}
.os-bar{display:flex;align-items:center;gap:8px;flex-wrap:wrap;}
.os-tab{height:30px;display:inline-flex;align-items:center;gap:7px;padding:0 10px;border:1px solid var(--os-border);
  border-radius:6px;background:#fff;color:var(--os-muted);font-size:13px;white-space:nowrap;}
.os-tab.on{border-color:var(--os-success);background:var(--os-success);color:#fff;}
.os-pill{padding:3px 9px;border-radius:999px;font-size:11px;font-weight:700;background:var(--os-panel);
  border:1px solid var(--os-border);color:var(--os-muted);}
.os-pill.ok{background:rgba(24,188,156,.12);border-color:rgba(24,188,156,.35);color:#128f76;}
.os-panel{border:1px solid var(--os-border);border-radius:8px;overflow:hidden;background:#fff;
  box-shadow:0 2px 5px rgba(0,0,0,.08);margin:0 0 16px;}
.os-panel-hd{padding:10px 12px;background:var(--os-panel);border-bottom:1px solid var(--os-border);
  font-weight:700;font-size:13px;color:var(--os-primary);}
.os-panel-bd{padding:12px 14px;font-size:13px;line-height:1.55;}
.os-table-wrap{border:1px solid var(--os-border);border-radius:8px;overflow:hidden;margin:14px 0;
  box-shadow:0 2px 5px rgba(0,0,0,.08);}
table{border-collapse:collapse;width:100%;font-size:13px;margin:0;}
.os-table-wrap table,.os-card table{margin:0;}
th,td{border:1px solid var(--os-border);padding:7px 10px;text-align:left;vertical-align:top;}
th{background:var(--os-panel);font-weight:700;color:var(--os-primary);}
tbody tr:hover td{background:var(--os-hover);}
pre,code{font:12px/1.5 ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;}
pre{padding:10px;background:var(--os-panel);border:1px solid var(--os-border);border-radius:6px;overflow:auto;}
`;

function buildSrcDoc(body: string): string {
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>${CANVAS_BASE_CSS}</style></head><body>${body}</body></html>`;
}

// The left-side display surface shown when no online environment tab is
// selected. Reads the latest fenced `alty_canvas` block from the chat
// (parsed in App.tsx) and renders its `html` body in a sandboxed iframe,
// or its `image` URL as a centered figure. Renders an explanatory empty
// state when the AI hasn't put anything on the canvas yet.
function CanvasHeaderBadge({ badge }: { badge: WorkspaceCanvasBadge }) {
  if (!badge) return null;
  const writing = badge === 'writing';
  return (
    <span
      className={`alty-canvas-badge${writing ? ' writing' : ' done'}`}
      role="status"
      aria-live="polite"
    >
      <i
        className={`fas ${writing ? 'fa-spinner fa-spin' : 'fa-check'}`}
        aria-hidden="true"
      />
      <span>{writing ? 'Updating canvas' : 'Canvas updated'}</span>
    </span>
  );
}

function CanvasHeaderControls({
  badge,
  onClose,
}: {
  badge: WorkspaceCanvasBadge;
  onClose?: () => void;
}) {
  return (
    <>
      <CanvasHeaderBadge badge={badge} />
      <span className="alty-canvas-live-pill">Live</span>
      {onClose ? (
        <button
          type="button"
          className="alty-icon-button alty-canvas-close"
          title="Close canvas"
          aria-label="Close canvas"
          onClick={onClose}
        >
          <i className="fas fa-times" aria-hidden="true" />
        </button>
      ) : null}
    </>
  );
}

export function WorkspaceCanvas({
  content,
  badge = null,
  multiEnv = false,
  onClose,
}: WorkspaceCanvasProps) {
  const srcDoc = useMemo(
    () => (content?.html ? buildSrcDoc(content.html) : null),
    [content?.html],
  );
  const title = content?.title?.trim() || 'Workspace canvas';
  const hasBody = !!(content?.html || content?.image);

  const tabLabel = title.length > 48 ? `${title.slice(0, 45)}…` : title;

  return (
    <div className="alty-canvas">
      <header className={`alty-canvas-head${multiEnv ? '' : ' alty-canvas-head--solo'}`}>
        {multiEnv ? (
          <>
            <p className="alty-canvas-section">Workspace</p>
            <div className="alty-canvas-bar">
              <div className="environment-tabs" role="tablist" aria-label="Workspace views">
                <span className="environment-tab active" role="tab" aria-selected="true">
                  <span className="environment-tab-dot" aria-hidden="true" />
                  <span className="alty-canvas-tab-label">{tabLabel}</span>
                </span>
              </div>
              <CanvasHeaderControls badge={badge} onClose={onClose} />
            </div>
          </>
        ) : (
          <div className="alty-canvas-bar">
            <p className="alty-canvas-section">Workspace</p>
            <div className="alty-canvas-bar-end">
              <CanvasHeaderControls badge={badge} onClose={onClose} />
            </div>
          </div>
        )}
      </header>
      <div className="alty-canvas-body">
        {hasBody ? (
          <>
            {srcDoc ? (
              <iframe
                className="alty-canvas-frame"
                title="Canvas content"
                sandbox="allow-same-origin"
                srcDoc={srcDoc}
              />
            ) : null}
            {content?.image && !srcDoc ? (
              <figure className="alty-canvas-figure">
                <img src={content.image} alt={content.caption || title} />
                {content.caption ? <figcaption>{content.caption}</figcaption> : null}
              </figure>
            ) : null}
          </>
        ) : (
          <div className="alty-canvas-empty">
            <i className="fas fa-paint-brush" aria-hidden="true" />
            <p>
              The canvas is empty. Ask the AI to render something here — a
              chart, a table, a mockup, or an image — and it'll appear here.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
